'use client';

import { useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { Calendar, FileText } from 'lucide-react';
import { LazyImage } from '../LazyImage';
import { CONTENT_TYPE_CONFIG } from '@sizzlenz/shared';
import type { ApiContent } from '@/lib/api/types';
import { getContentDisplayDate, getContentUrl } from '@/lib/utils';

/** Content types that should not display a date on the card. */
const HIDE_DATE_TYPES: string[] = ['hub'];

export interface ContentCardProps {
  content: ApiContent;
  variant?: 'default' | 'compact';
  /** Override the link destination. Defaults to `/content/{slug || id}`. */
  href?: string;
  /** Label for the "Back to..." link shown on the detail page (passed as query param). */
  backLabel?: string;
  /** URL for the "Back to..." link shown on the detail page (passed as query param). */
  backHref?: string;
  /** Show the content type badge on the image. Defaults to `false`. */
  showTypeBadge?: boolean;
  /** Show tags below the blurb. Defaults to `false`. */
  showTags?: boolean;
  /** Show the date/when row. Defaults to `true`. */
  showWhen?: boolean;
  /** Show the image/avatar area. Defaults to `true`. */
  showImage?: boolean;
}

function formatDate(d: string | null | undefined) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function buildHref(base: string, backLabel?: string, backHref?: string): string {
  if (!backLabel && !backHref) return base;
  const params = new URLSearchParams();
  if (backLabel) params.set('backLabel', backLabel);
  if (backHref) params.set('backHref', backHref);
  return `${base}?${params.toString()}`;
}

export function ContentCard({
  content,
  variant = 'default',
  href,
  backLabel,
  backHref,
  showTypeBadge = false,
  showTags = false,
  showWhen = true,
  showImage = true,
}: ContentCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isCompact = variant === 'compact';

  const typeConfig = CONTENT_TYPE_CONFIG[content.type as keyof typeof CONTENT_TYPE_CONFIG];
  const baseHref = href || getContentUrl(content);
  const linkHref = buildHref(baseHref, backLabel, backHref);

  return (
    <Link href={linkHref} className="h-full">
      <div
        className={clsx(
          'group relative bg-white overflow-hidden hover:shadow-xl cursor-pointer h-full flex flex-col',
          isCompact ? 'rounded-xl' : 'rounded-2xl',
          !isCompact && 'border border-gray-100'
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image */}
        {showImage && (
          <div className="relative overflow-hidden bg-gray-50">
            <LazyImage
              src={content.image || ''}
              alt={content.title || 'Content'}
              aspectRatio={isCompact ? '16/9' : '3/2'}
              className={clsx(
                'w-full',
                isCompact ? 'h-[200px]' : 'h-[240px]',
                isHovered && 'scale-105 transition-transform duration-500'
              )}
              objectFit="cover"
              fallback={
                <div
                  className={clsx(
                    'flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50',
                    isCompact ? 'h-[140px]' : 'h-[200px]'
                  )}
                >
                  <FileText
                    className={clsx('text-blue-300', isCompact ? 'w-8 h-8' : 'w-12 h-12')}
                  />
                </div>
              }
            />

            {/* Type badge (overlaid on image) */}
            {showTypeBadge && typeConfig && (
              <span
                className={clsx(
                  'absolute rounded-full font-semibold shadow-sm z-10',
                  isCompact
                    ? 'top-2 right-2 text-[10px] px-2 py-0.5'
                    : 'top-3 right-3 text-xs px-3 py-1',
                  typeConfig.color
                )}
              >
                {typeConfig.label}
              </span>
            )}
          </div>
        )}

        {/* Type badge (inline when image is hidden) */}
        {!showImage && showTypeBadge && typeConfig && (
          <div className={clsx(isCompact ? 'px-3 pt-3' : 'px-5 pt-4')}>
            <span
              className={clsx(
                'inline-block rounded-full font-semibold',
                isCompact ? 'text-[10px] px-2 py-0.5' : 'text-xs px-3 py-1',
                typeConfig.color
              )}
            >
              {typeConfig.label}
            </span>
          </div>
        )}

        {/* Body */}
        <div className={clsx('flex-1', isCompact ? 'p-3' : 'p-5')}>
          {/* Date */}
          {showWhen &&
            (() => {
              if (HIDE_DATE_TYPES.includes(content.type)) return null;
              const displayDate = getContentDisplayDate(content);
              return displayDate ? (
                <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-2">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{formatDate(displayDate)}</span>
                </div>
              ) : null;
            })()}

          <h3
            className={clsx(
              'font-bold text-gray-900 line-clamp-3 group-hover:text-blue-600 transition-colors',
              isCompact ? 'text-sm mb-1' : 'text-[17px] leading-tight mb-2'
            )}
          >
            {content.title || 'Untitled'}
          </h3>

          {/* Blurb */}
          {content.blurb && !isCompact && (
            <p className="text-sm text-gray-600 line-clamp-3 mb-2">{content.blurb}</p>
          )}

          {/* Tags */}
          {showTags && content.tags && content.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {content.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="bg-gray-100 text-gray-600 text-[11px] px-2.5 py-1 rounded-md font-medium"
                >
                  {tag}
                </span>
              ))}
              {content.tags.length > 3 && (
                <span className="bg-gray-50 text-gray-500 text-[11px] px-2.5 py-1 rounded-md font-medium">
                  +{content.tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
